/**
 * History for a record opened over a list (decisions 28 a and 29 a): opening it from the list
 * adds an entry, switching to another record replaces it, a view inside the record (edit,
 * history) adds one, and closing goes Back to the list, so Back afterwards leaves the page as in
 * Gmail.
 *
 * The record's entries carry a mark in their history state: how many entries it is past the list,
 * and the list's URL when it was opened. A record opened by a link has no list behind it, and a
 * list whose filters changed while the record was open no longer matches the entry behind it;
 * either way closing replaces the entry with the list instead of going Back.
 */
export type RecordMark = {
  /** Entries since the list: 1 for the record, 2 for a view inside it. */
  depth: number;
  /** The list's URL (path and query) the record was opened from; null when opened by a link. */
  list: string | null;
  /** For a view inside the record: the record's URL it was opened from. */
  parent?: string | null;
};

const STATE_KEY = 'sfRecord';

export function readRecordMark(state: unknown): RecordMark | null {
  const mark = (state as Record<string, unknown> | null)?.[STATE_KEY] as RecordMark | undefined;
  if (!mark || !Number.isInteger(mark.depth) || mark.depth < 1) return null;
  return {
    depth: mark.depth,
    list: typeof mark.list === 'string' ? mark.list : null,
    parent: typeof mark.parent === 'string' ? mark.parent : null,
  };
}

export function recordState(mark: RecordMark | null): Record<string, unknown> | undefined {
  return mark ? { [STATE_KEY]: mark } : undefined;
}

/** A navigation: go to `to` (push or replace, with `mark` in its state), or go back `back`. */
export type RecordNavigation =
  | { to: string; replace: boolean; mark: RecordMark | null }
  | { back: number };

/** Open a record. `isOpen`: one is open already, so this switches (replaces). */
export function openRecord(mark: RecordMark | null, isOpen: boolean, to: string, listHref: string): RecordNavigation {
  if (isOpen) return { to, replace: true, mark };
  return { to, replace: false, mark: { depth: 1, list: listHref } };
}

/** A view inside the open record (edit, history), opened from `here` (the record's URL), which Back leaves. */
export function openWithinRecord(mark: RecordMark | null, to: string, here: string): RecordNavigation {
  return {
    to,
    replace: false,
    mark: { depth: (mark?.depth ?? 0) + 1, list: mark?.list ?? null, parent: here },
  };
}

/**
 * Leave a view inside the record for the record at `to` (Cancel, Done, a Back link): Back when
 * the entry behind is that URL, otherwise (it was opened by a link, or the list's filters changed
 * meanwhile) replace, so nothing changed on the way is lost.
 */
export function backWithinRecord(mark: RecordMark | null, to: string): RecordNavigation {
  const recordDepth = mark?.list ? 1 : 0;
  if (mark && mark.depth > recordDepth && mark.parent === to) return { back: 1 };
  return { to, replace: true, mark: mark && { ...mark, parent: null } };
}

/** Close the record for the list at `listHref` (the list as it is now). */
export function closeRecord(mark: RecordMark | null, listHref: string): RecordNavigation {
  if (mark?.list === listHref) return { back: mark.depth };
  return { to: listHref, replace: true, mark: null };
}
